import React, { createContext, useContext, useRef } from "react";
import ApplicationContext from "../ApplicationContext";
import { SiteLogType } from "../fms.enum";
import { JobNames } from "../framework.core/JobNames";

type ProductType = "ISF" | "AES" | "INBOND";
type FilingType = "10" | "5";

interface RunAceFilingParams {
  aceType: ProductType;
  jobId: string;
  jobName: string;
  jobType?: FilingType; // ISF Only
  hblNo?: string,
}

type AceBrowserContextType = {
  aceBrowserRef: React.MutableRefObject<Record<ProductType, Window | null>>;
  setAceBrowser: (type: ProductType, win: Window | null) => void;
};

const AceBrowserContext = createContext<AceBrowserContextType | undefined>(undefined);

export const AceBrowserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Ref로 브라우저 창 추적
  const aceBrowserRef = useRef<Record<ProductType, Window | null>>({
    ISF: null,
    AES: null,
    INBOND: null,
  });

  const setAceBrowser = (type: ProductType, win: Window | null) => {
    aceBrowserRef.current[type] = win;
  };

  return (
    <AceBrowserContext.Provider value={{ aceBrowserRef, setAceBrowser }}>
      {children}
    </AceBrowserContext.Provider>
  );
};

export const useAceBrowser = () => {
  const context = useContext(AceBrowserContext);
  if (!context) throw new Error("useAceBrowser must be used within AceBrowserProvider");
  return context;
};

export const useRunAceFiling = () => {
  const { aceBrowserRef, setAceBrowser } = useAceBrowser();
  const appContext = useContext(ApplicationContext);

  return async ({ aceType, jobId, jobName, jobType, hblNo }: RunAceFilingParams) => {
    const existingWindow = aceBrowserRef.current[aceType];

    // 1. 이미 열려 있고 닫히지 않았다면 포커스
    if (existingWindow && !existingWindow.closed) {
      existingWindow.focus();
      return;
    }

    // 2. 서버 호출
    let apiResult: any = null;
    let width = 800;
    let height = 600;
    let url = '';

    switch (aceType) {
      case "ISF":
        apiResult = await appContext.api.getIsfFilingLink(jobName, jobId, jobType);
        width = 1024;
        height = jobType === "10" ? 640 : 570;
        url = 'https://acefiler.suntekscm.com/ace/';
        break;
      case "AES":
        apiResult = await appContext.api.getAesFilingLink(jobName, jobId);
        width = 700 + 16;
        height = (jobName === JobNames.OEHbl ? 524 : 497) + 38;
        url = 'https://aes.suntekscm.com/ace/';
        break;
      // case "INBOND":
      //   apiResult = await appContext.api.getInbondFilingLink(jobName, jobId);
      //   break;
      default:
        console.error("Unsupported ACE type:", aceType);
        return;
    }

    if (apiResult && apiResult.success) {
      url += apiResult.result;

      if(!appContext.user?.isSuntekAdmin && aceType === 'ISF') await appContext.api.saveSiteAccessLog(SiteLogType.IsfFiling, 1, hblNo);

      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const newWindow = window.open(
        url,
        `${aceType.toLowerCase()}Window`, 
        `width=${width},height=${height},left=${left},top=${top},resizable=no,scrollbars=no`
      );

      setAceBrowser(aceType, newWindow);
    }
  };
};